/**
 * Flyer Prompt Builders
 * Each function builds a prompt for a specific flyer design template
 *
 * IMPORTANT FOR ALL FLYERS:
 * - Do NOT print label words like "Title", "Description", or "Details" in the design.
 *   Only use the CONTENT provided in those fields.
 * - If color hex codes are provided, use them for shapes and accents but NEVER print
 *   the hex codes as visible text in the flyer.
 * - If a client reference image is attached to the request, follow CASE 1.
 *   If not, follow CASE 2 (text-only, centered layout).
 */

/**
 * FLYER 1: Poise Style - Clean, modern professional design
 */
export function buildFlyer1Prompt({
  INDUSTRY,
  TITLE,
  DESCRIPTION,
  DETAILS,
  PHONE,
  WEBSITE,
  PRIMARY_COLOR,
  SECONDARY_COLOR
}) {
  const hasDetails = DETAILS && DETAILS.trim().length > 0;
  const hasPhone = PHONE && PHONE.trim().length > 0;
  const hasWebsite = WEBSITE && WEBSITE.trim().length > 0;
  const hasPrimary = PRIMARY_COLOR && PRIMARY_COLOR.trim().length > 0;
  const hasSecondary = SECONDARY_COLOR && SECONDARY_COLOR.trim().length > 0;

  let prompt = '';

  // Header / dynamic content section
  prompt += `Create a clean, modern 1080x1080 social media flyer for a ${INDUSTRY} industry.\n\n`;
  prompt += `IMPORTANT CANVAS CONSTRAINTS:\n`;
  prompt += `- The final generated image MUST be a perfect square.\n`;
  prompt += `- The canvas size must be 1080x1080 (1:1 aspect ratio).\n`;
  prompt += `- Do NOT create tall/portrait or wide/landscape banners. Always keep the full design inside a square 1080x1080 canvas.\n\n`;

  prompt += `Use the following dynamic content (do NOT show label words like "Title", "Description", or "Details" in the design, only use their content):\n\n`;
  prompt += `Title (main headline): ${TITLE}\n`;
  prompt += `Description (intro paragraph): ${DESCRIPTION}\n`;

  if (hasDetails) {
    prompt += `Details (optional extra text block): ${DETAILS}.\n`;
  }

  if (hasPrimary || hasSecondary) {
    prompt += `Colors: use the custom colors provided — primary ${
      PRIMARY_COLOR || 'primary color'
    }, secondary ${
      SECONDARY_COLOR || 'secondary color'
    }. Use these colors for backgrounds, shapes, buttons, and accents, but do NOT print the hex codes as visible text.\n`;
  } else {
    prompt += `Colors: if no custom colors are provided, use the default palette described below.\n`;
  }

  prompt += `Logo (optional image): if a logo image is provided, use it as described below. If no logo image is attached, do NOT invent or draw a fake logo; instead, use the TITLE text as the main brand element in the header.\n`;

  if (hasPhone) {
    prompt += `Phone (optional): ${PHONE}\n`;
  }
  if (hasWebsite) {
    prompt += `Website (optional): ${WEBSITE}\n`;
  }

  // Layout & composition
  prompt += `\nLayout and composition:\n\n`;
  prompt += `Canvas: 1080x1080, with a light, airy background in soft off-white, with subtle diagonal light-gray shapes for depth. All elements must fit inside this square canvas without extending it.\n\n`;
  prompt += `At the top center, create the brand/header area:\n`;
  prompt += `- If a logo image is provided, place the logo at the top center in a clean, elegant style.\n`;
  prompt += `- If no logo image is provided, place the TITLE text at the top center as the main brand text, using a refined serif or clean sans-serif font.\n\n`;
  prompt += `Directly under the brand/header text, add a small, elegant script-style tagline (for example "Join Our Team" or another short phrase that matches the tone of the main headline). This tagline should be subtle and not overpower the main title.\n\n`;
  prompt += `Under the script line, place the main headline using the TITLE content in large, bold, uppercase sans-serif text. It can be centered or slightly left-aligned, but it must be clearly the most prominent text.\n\n`;

  // Description + details block
  prompt += `On the left side of the flyer, beneath the main headline, place the description paragraph using the DESCRIPTION content.\n`;
  prompt += `Use a dark gray, easy-to-read sans-serif font. Keep line width comfortable and maintain good spacing.\n`;
  prompt += `If the DESCRIPTION text is long, reduce the font size slightly and allow more lines; if it is short, use a larger font size and generous line spacing to keep it visually balanced.\n\n`;

  if (hasDetails) {
    prompt += `Below the description on the left, place a separate details block using the DETAILS content.\n`;
    prompt += `Do NOT add a "Details" heading; simply show the content as an additional text block.\n`;
    prompt += `If the DETAILS content includes multiple lines or items, you may format them as a vertical list with subtle bullet points. Preserve line breaks from the DETAILS content so pricing tiers, schedules, or other structured information stay well organized.\n`;
    prompt += `If the DETAILS text is long, use a slightly smaller font size than the main description to keep the hierarchy clear.\n\n`;
  }

  // Image usage: CASE 1 (has reference image) / CASE 2 (no image)
  prompt += `Image usage and layout:\n`;
  prompt += `There are two possible cases for this flyer, depending on whether a client reference image is attached to this request.\n\n`;

  // CASE 1 – with reference image
  prompt += `CASE 1 – A client reference image IS attached:\n`;
  prompt += `- On the right side of the flyer, from mid-height downwards, create a tall rounded-rectangle photo area.\n`;
  prompt += `- Place the attached client image inside this rounded-rectangle.\n`;
  prompt += `- You MAY CROP or ZOOM the client image so it fits nicely in the rounded-rectangle without distorting it.\n`;
  prompt += `- You do NOT need to show the entire original image; showing the most relevant central part is enough.\n`;
  prompt += `- Do NOT stretch the image or change the overall canvas ratio. Keep the client image fully inside the 1080x1080 square composition.\n`;
  prompt += `- Ensure that the text on the left does not touch or overlap the photo; keep a clear margin between text and image.\n\n`;

  // CASE 2 – no image
  prompt += `CASE 2 – NO client image is attached:\n`;
  prompt += `- Do NOT generate any photo, illustration, or stock-like image.\n`;
  prompt += `- Remove the right-side photo column and use a single-column layout instead.\n`;
  prompt += `- Center the entire content on the canvas horizontally.\n`;
  prompt += `- Place the logo/brand (or TITLE text if there is no logo image) at the top center.\n`;
  prompt += `- Under it, center the main headline using the TITLE content in large, bold, uppercase sans-serif text.\n`;
  prompt += `- Below the headline, center the DESCRIPTION paragraph.\n`;
  if (hasDetails) {
    prompt += `- If DETAILS are provided, place them under the description as a separate block (paragraph or simple list), also centered.\n`;
  }
  if (hasPhone || hasWebsite) {
    prompt += `- Place the contact info (phone/website) and the CTA button centered near the bottom of the flyer.\n`;
  }
  prompt += `- Use subtle diagonal background shapes and the color palette described, but keep the composition clean and focused on the text with generous white space.\n\n`;

  // Contact block + CTA
  if (hasPhone || hasWebsite) {
    prompt += `In either layout (with or without image), include a contact section:\n`;
    prompt += `- In the normal two-column layout (CASE 1), place the contact/info block in the bottom-left area.\n`;
    if (hasPhone) {
      prompt += `  • Include a circular phone icon followed by the phone number ${PHONE} in clear sans-serif text.\n`;
    }
    if (hasWebsite) {
      prompt += `  • Include a small globe or link icon followed by the website ${WEBSITE}.\n`;
    }
    prompt += `- Add a clear call-to-action button whose text depends on which contact data is available:\n`;
    if (hasPhone && hasWebsite) {
      prompt += `  • If both phone and website are provided, use button text like "CONTACT US" and visually relate it to both phone and website.\n`;
    } else if (hasPhone) {
      prompt += `  • If only the phone number is provided, use button text like "CALL NOW" associated with the phone number.\n`;
    } else if (hasWebsite) {
      prompt += `  • If only the website is provided, use button text like "VISIT WEBSITE" or "LEARN MORE" associated with the website.\n`;
    }
    prompt += `The CTA button should be a rounded pill filled with the primary color, with bold, uppercase white sans-serif text.\n\n`;
  }

  // Style & colors
  prompt += `Style & colors:\n\n`;
  prompt += `Default color palette if none is provided:\n`;
  prompt += `- Primary: soft slate / pastel blue.\n`;
  prompt += `- Secondary: very light gray / off-white for background shapes.\n`;
  prompt += `- Accent: soft blush pink or another subtle accent tone that fits the ${INDUSTRY}.\n`;
  prompt += `- Body text: dark charcoal gray or near-black.\n\n`;
  prompt += `If custom primary and/or secondary colors are provided, adapt all shapes, buttons, and accents to use those colors while keeping high contrast and readability. Do not show the hex codes in the artwork.\n\n`;
  prompt += `Overall look: professional, welcoming, and modern. Clean layout, generous white space, subtle shadows, and soft rounded corners.\n`;
  prompt += `Typography: mix an elegant script font for the small tagline with a modern bold sans-serif for the main headline, and a simple sans-serif for the description and details text.\n`;
  prompt += `Keep the design flat and minimal, with clear hierarchy: logo/brand → tagline → main headline → description → details (if present) → contact block (if present) → CTA button (if present).\n`;

  return prompt;
}

/**
 * FLYER 2: Service Panel Style - Left content panel + right image
 */
export function buildFlyer2Prompt({
  INDUSTRY,
  TITLE,
  DESCRIPTION,
  DETAILS,
  PHONE,
  WEBSITE,
  PRIMARY_COLOR,
  SECONDARY_COLOR
}) {
  const hasDetails = DETAILS && DETAILS.trim().length > 0;
  const hasPhone = PHONE && PHONE.trim().length > 0;
  const hasWebsite = WEBSITE && WEBSITE.trim().length > 0;
  const hasPrimary = PRIMARY_COLOR && PRIMARY_COLOR.trim().length > 0;
  const hasSecondary = SECONDARY_COLOR && SECONDARY_COLOR.trim().length > 0;

  let prompt = '';

  prompt += `Create a modern 1080x1080 social media flyer for a ${INDUSTRY} industry, with a split layout: a content panel on the left and an optional image area on the right.\n\n`;
  prompt += `IMPORTANT CANVAS CONSTRAINTS:\n`;
  prompt += `- The final generated image MUST be a perfect square.\n`;
  prompt += `- The canvas must be 1080x1080 (1:1 aspect ratio).\n`;
  prompt += `- Do NOT make the design look like a tall poster or a wide banner; keep everything inside a square.\n\n`;

  prompt += `Use the following dynamic content (do NOT show label words like "Title", "Description", or "Details" in the design, only use their content):\n\n`;
  prompt += `Title (main headline): ${TITLE}\n`;
  prompt += `Description (intro or highlight sentence): ${DESCRIPTION}\n`;

  if (hasDetails) {
    prompt += `Details (optional, can be paragraph or list): ${DETAILS}.\n`;
  }

  if (hasPrimary || hasSecondary) {
    prompt += `Colors: use the custom colors provided — primary ${
      PRIMARY_COLOR || 'primary color'
    }, secondary ${
      SECONDARY_COLOR || 'secondary color'
    }. Apply them to the left panel background, badges, icons, and CTA, but do NOT print hex codes in the design.\n`;
  } else {
    prompt += `Colors: if no custom colors are provided, use a default palette with a soft colored panel on the left and neutral off-white on the right, as described below.\n`;
  }

  prompt += `Logo (optional image): if a logo image is provided, place it in the upper-left corner of the content panel. If no logo image is attached, you may use the TITLE text as the main brand element there, but do NOT invent a graphic logo.\n`;

  if (hasPhone) {
    prompt += `Phone (optional): ${PHONE}\n`;
  }
  if (hasWebsite) {
    prompt += `Website (optional): ${WEBSITE}\n`;
  }

  // Layout & composition
  prompt += `\nLayout and composition:\n\n`;
  prompt += `Canvas: 1080x1080. All panels and image areas must fit inside this square canvas.\n`;
  prompt += `Divide the canvas into two vertical sections INSIDE the 1080x1080 square:\n`;
  prompt += `- Left: a solid or softly textured colored panel for text and branding.\n`;
  prompt += `- Right: an area reserved for the client image if available.\n`;
  prompt += `The division is internal; do NOT stretch the canvas to be wider than square.\n\n`;

  prompt += `On the LEFT panel:\n`;
  prompt += `- Place the logo (if provided) or the TITLE text near the top as brand.\n`;
  prompt += `- Below that, show the main headline using the TITLE content in bold, high-contrast sans-serif text.\n`;
  prompt += `- Under the headline, show the DESCRIPTION as a short paragraph or a couple of lines.\n`;
  prompt += `  If DESCRIPTION is long, slightly reduce the font size and let it wrap; if short, use larger text and more spacing.\n\n`;

  if (hasDetails) {
    prompt += `- Below the description, render the DETAILS content as an additional block:\n`;
    prompt += `  • If the DETAILS content looks like a list (for example, multiple lines, bullet characters like "-" or "•", or clearly separated items), format it as a vertical bullet list with simple round bullets.\n`;
    prompt += `  • Otherwise, render DETAILS as a normal paragraph or two, without bullets.\n`;
    prompt += `  • Preserve line breaks from the DETAILS content so structured information (services, features, prices, etc.) stays organized.\n\n`;
  }

  // Image usage: CASE 1 / CASE 2
  prompt += `On the RIGHT side (image area):\n`;
  prompt += `There are two possible cases for this flyer, depending on whether a client reference image is attached.\n\n`;

  prompt += `CASE 1 – A client reference image IS attached:\n`;
  prompt += `- Use the attached client image as the main visual in the right section.\n`;
  prompt += `- You MAY CROP or ZOOM the client image so it fits nicely into a vertical rectangle inside the right half of the 1080x1080 square.\n`;
  prompt += `- Do NOT stretch or distort the image; maintain its proportions.\n`;
  prompt += `- You do not need to show the entire original image; focus on the most relevant part for the flyer.\n`;
  prompt += `- Keep clear margins between the photo and the edges of the square canvas and between the photo and the text panel on the left.\n\n`;

  prompt += `CASE 2 – NO client image is attached:\n`;
  prompt += `- Do NOT generate any photo or illustration.\n`;
  prompt += `- Remove the strict split layout and instead create a single-column text layout.\n`;
  prompt += `- Center all content horizontally on the 1080x1080 canvas: brand (logo or TITLE), headline, DESCRIPTION, DETAILS (if present), and CTA/contact.\n`;
  prompt += `- Use subtle background shapes and color blocks to keep a sense of structure, but rely entirely on typography and shapes rather than imagery.\n\n`;

  // Contact info + CTA
  if (hasPhone || hasWebsite) {
    prompt += `Contact & CTA:\n`;
    prompt += `- Add a call-to-action button near the bottom of the left panel (or centered near the bottom in the text-only layout).\n`;
    prompt += `- Use the primary color for the button fill and white or very dark text with bold, uppercase sans-serif lettering.\n`;
    if (hasPhone && hasWebsite) {
      prompt += `- Button text can be "CONTACT US" or similar, relating to both phone and website.\n`;
    } else if (hasPhone) {
      prompt += `- Button text can be "CALL NOW" and relate to the phone number.\n`;
    } else if (hasWebsite) {
      prompt += `- Button text can be "VISIT WEBSITE" or "LEARN MORE" and relate to the website.\n`;
    }

    prompt += `Below or beside the button, display the available contact info:\n`;
    if (hasPhone) {
      prompt += `  • Phone: show ${PHONE} with a small phone icon.\n`;
    }
    if (hasWebsite) {
      prompt += `  • Website: show ${WEBSITE} with a small globe or link icon.\n`;
    }
    prompt += `Ensure the contact text is readable and not cramped.\n\n`;
  }

  // Style & colors
  prompt += `Style & colors:\n`;
  prompt += `- Default primary: a calm accent color for the left panel and CTA.\n`;
  prompt += `- Default secondary: a soft contrasting color for small shapes or badges.\n`;
  prompt += `- Background: light neutral tone (off-white) behind the right-side image or, in text-only mode, behind the centered text.\n`;
  prompt += `- Text: dark charcoal gray or black for readability.\n`;
  prompt += `If custom colors are provided, use them for the left panel and CTA elements, but never show hex codes in the artwork.\n`;

  return prompt;
}

/**
 * FLYER 3: Home Care Style - Curved photo header + contact bar
 */
export function buildFlyer3Prompt({
  INDUSTRY,
  TITLE,
  DESCRIPTION,
  DETAILS,
  PHONE,
  WEBSITE,
  PRIMARY_COLOR,
  SECONDARY_COLOR
}) {
  const hasDetails = DETAILS && DETAILS.trim().length > 0;
  const hasPhone = PHONE && PHONE.trim().length > 0;
  const hasWebsite = WEBSITE && WEBSITE.trim().length > 0;
  const hasPrimary = PRIMARY_COLOR && PRIMARY_COLOR.trim().length > 0;
  const hasSecondary = SECONDARY_COLOR && SECONDARY_COLOR.trim().length > 0;

  let prompt = '';

  // Header / dynamic content section
  prompt += `Create a clean, caring 1080x1080 social media flyer for a ${INDUSTRY} industry, using a curved top image header and a strong contact bar at the bottom.\n\n`;
  prompt += `Use the following dynamic content (do NOT show label words like "Title", "Description", or "Details" in the design, only use their content):\n\n`;
  prompt += `Title (main headline): ${TITLE}\n`;
  prompt += `Description (supporting line or short paragraph): ${DESCRIPTION}\n`;

  if (hasDetails) {
    prompt += `Details (optional extra text block): ${DETAILS}.\n`;
  }

  if (hasPrimary || hasSecondary) {
    prompt += `Colors: use the custom colors provided — primary ${
      PRIMARY_COLOR || 'primary color'
    }, secondary ${
      SECONDARY_COLOR || 'secondary color'
    }. Apply them to the wave accent, footer bar, and CTA button, but do NOT print hex codes in the design.\n`;
  } else {
    prompt += `Colors: if no custom colors are provided, use the default palette described below.\n`;
  }

  prompt += `Logo (optional image): if a logo image is provided, place it in a logo area under the curved photo as described. If no logo image is provided, do NOT invent or stylize a separate logo; instead, only use the TITLE once as the main headline.\n`;

  if (hasPhone) {
    prompt += `Phone (optional): ${PHONE}\n`;
  }
  if (hasWebsite) {
    prompt += `Website (optional): ${WEBSITE}\n`;
  }

  // Layout & composition
  prompt += `\nLayout and composition:\n\n`;
  prompt += `Canvas: 1080x1080.\n\n`;

  // Image usage: curved header, CASE 1 / CASE 2
  prompt += `Top section – curved image header:\n`;
  prompt += `There are two possible cases, depending on whether a client reference image is attached.\n\n`;

  prompt += `CASE 1 – A client reference image IS attached:\n`;
  prompt += `- At the top of the flyer, create a large rectangular photo area that spans the full width.\n`;
  prompt += `- Use the attached client image as this top photo. Do NOT replace it with another generated scene.\n`;
  prompt += `- The bottom edge of this photo should have a smooth, flowing curved or wave-shaped cut-out that transitions into the white space below.\n`;
  prompt += `- Add a soft band using the secondary color along the curve to emphasize the transition.\n\n`;

  prompt += `CASE 2 – NO client image is attached:\n`;
  prompt += `- Do NOT generate any photo.\n`;
  prompt += `- Instead of a photo, create a curved band of color at the top using the primary or secondary color, with a similar wave-like shape.\n`;
  prompt += `- This curved color band serves as a graphic header, but the flyer remains entirely typographic (no pictures).\n\n`;

  // Logo + title + description (center/left)
  prompt += `Below the curved header (image or color band), on the white background, create the main text area:\n`;
  prompt += `- If a logo image is provided, place it on the left below the curve at a comfortable size.\n`;
  prompt += `- If no logo image is provided, do NOT add a separate logo word; instead, use the TITLE as the first and only main heading.\n\n`;

  prompt += `Render the main headline using the TITLE content in bold uppercase sans-serif text, left-aligned, using the primary color. It must appear only once in the design as the main heading.\n\n`;

  prompt += `Below the main headline, add the DESCRIPTION as one or two lines of dark gray text, left-aligned on the white background.\n`;
  prompt += `If the DESCRIPTION is long, slightly reduce its font size; if short, use a larger font size and generous spacing.\n\n`;

  if (hasDetails) {
    prompt += `If DETAILS content is provided, place it below the description as an extra text block in smaller sans-serif text.\n`;
    prompt += `Preserve line breaks from the DETAILS content to separate different points (service notes, areas served, or additional benefits).\n`;
    prompt += `Keep this block simple and readable, without heavy list icons.\n\n`;
  }

  // Bottom contact bar
  prompt += `At the bottom of the flyer, add a full-width horizontal bar in the primary color.\n`;

  if (hasPhone || hasWebsite) {
    prompt += `Inside this bottom bar:\n`;
    prompt += `- On the left side, place a rounded pill-style call-to-action button filled with a lighter tint or secondary color, with bold uppercase text such as "CONTACT US". The text color should contrast with the button color.\n`;
    prompt += `- On the right side, place the contact information.\n`;
    if (hasWebsite) {
      prompt += `  • Include a small globe or web icon followed by the website ${WEBSITE} in white sans-serif text.\n`;
    }
    if (hasPhone) {
      prompt += `  • Include a phone-related icon followed by the phone number ${PHONE} in white sans-serif text.\n`;
    }
    prompt += `Arrange these elements side by side or stacked in a visually balanced way, ensuring high readability against the primary color.\n\n`;
  } else {
    prompt += `If no phone or website is provided, keep the bottom bar as a simple solid band of color with no text, to anchor the design.\n\n`;
  }

  // Style & colors
  prompt += `Style & colors:\n\n`;
  prompt += `Default color palette if none is provided:\n`;
  prompt += `- Primary: medium to deep blue for headlines, footer bar, and key accents.\n`;
  prompt += `- Secondary: light sky blue or soft lavender for the wave accent and CTA button background.\n`;
  prompt += `- Background: clean white for the main content area.\n`;
  prompt += `- Body text: dark charcoal gray or near-black.\n\n`;
  prompt += `If custom primary and/or secondary colors are provided, apply them consistently to the curved header band (or around the attached image), CTA button, headline text, and footer bar while keeping strong contrast and legibility. Do not show hex codes in the artwork.\n\n`;
  prompt += `Overall look: caring, trustworthy, and professional. Use soft curves, generous white space, and calm colors to convey comfort and reliability.\n`;
  prompt += `Use clean sans-serif typography for most text, with strong weight for the title and lighter weight for description and details.\n`;
  prompt += `Keep the design flat and minimal, with a clear hierarchy from the top header (image or color band) to the single main headline, followed by description, details (if present), and the contact-focused bottom bar.\n`;

  return prompt;
}

/**
 * FLYER 4: Electrical Promo Style - Bold discount + circular photo & gold accents
 */
export function buildFlyer4Prompt({
  INDUSTRY,
  TITLE,
  DESCRIPTION,
  DETAILS,
  PHONE,
  WEBSITE,
  PRIMARY_COLOR,
  SECONDARY_COLOR
}) {
  const hasDetails = DETAILS && DETAILS.trim().length > 0;
  const hasPhone = PHONE && PHONE.trim().length > 0;
  const hasWebsite = WEBSITE && WEBSITE.trim().length > 0;
  const hasPrimary = PRIMARY_COLOR && PRIMARY_COLOR.trim().length > 0;
  const hasSecondary = SECONDARY_COLOR && SECONDARY_COLOR.trim().length > 0;

  let prompt = '';

  // Header / dynamic content section
  prompt += `Create a bold, modern 1080x1080 promotional social media flyer for a ${INDUSTRY} industry, focused on a special discount or offer.\n\n`;
  prompt += `IMPORTANT CANVAS CONSTRAINTS:\n`;
  prompt += `- The final generated image MUST be a perfect square.\n`;
  prompt += `- The canvas must be 1080x1080 (1:1 aspect ratio) and should NOT become a tall poster or a wide banner.\n`;
  prompt += `- All shapes, text, and any reference image must be composed inside this square.\n\n`;

  prompt += `Use the following dynamic content (do NOT show label words like "Title", "Description", or "Details" in the design, only use their content):\n\n`;
  prompt += `Title (main headline or key promo text): ${TITLE}\n`;
  prompt += `Description (supporting sentence or explanation): ${DESCRIPTION}\n`;

  if (hasDetails) {
    prompt += `Details (optional extra text block, e.g. conditions or extra info): ${DETAILS}.\n`;
  }

  if (hasPrimary || hasSecondary) {
    prompt += `Colors: use the custom colors provided — primary ${
      PRIMARY_COLOR || 'primary color'
    }, secondary ${
      SECONDARY_COLOR || 'secondary color'
    }. Apply them to corner shapes, the CTA button, and key accents. Do NOT print hex codes on the design.\n`;
  } else {
    prompt += `Colors: if no custom colors are provided, use the default palette described below.\n`;
  }

  prompt += `Logo (optional image): if a logo image is provided, place it in the top-left logo area. If no logo image is provided, do NOT invent a graphic logo; instead, place the TITLE text in the top-left area as a simple brand label.\n`;

  if (hasPhone) {
    prompt += `Phone (optional): ${PHONE}\n`;
  }
  if (hasWebsite) {
    prompt += `Website (optional): ${WEBSITE}\n`;
  }

  // Layout & composition
  prompt += `\nLayout and composition:\n\n`;
  prompt += `Canvas: 1080x1080, with a primarily white background and strong yellow/gold accent areas. Keep all elements inside this square.\n\n`;

  prompt += `At the top-left corner on a white background, create the brand area:\n`;
  prompt += `- If a logo image is provided, place the logo there at a comfortable size.\n`;
  prompt += `- If no logo is provided, place the TITLE text here in a bold, simple sans-serif style.\n\n`;

  prompt += `In the top-right and bottom-right corners, add overlapping curved shapes in the secondary color and a deeper shade of it, creating a layered, modern look.\n\n`;

  // Main promo text on the left
  prompt += `On the left half of the flyer, below the brand area, place the main promotional message using the TITLE content:\n`;
  prompt += `- Emphasize the key part of the offer (for example, a percentage discount) in very large, bold sans-serif text using the primary color.\n`;
  prompt += `- Place supporting parts of the TITLE in slightly smaller bold dark gray or black text, stacked vertically and left-aligned.\n\n`;

  prompt += `Below the main title block, add the DESCRIPTION as a concise line or two in regular-weight sans-serif text, aligned with the headline.\n`;
  prompt += `If TEXT is long (TITLE + DESCRIPTION + DETAILS), slightly reduce font sizes and increase spacing; if short, use larger text and generous spacing.\n\n`;

  if (hasDetails) {
    prompt += `If DETAILS are provided, place them beneath the description in smaller sans-serif text.\n`;
    prompt += `Preserve line breaks in the DETAILS so any conditions, notes, or extra information appear as separate lines, but do not add a literal "Details" heading.\n\n`;
  }

  // Circular photo on the right: CASE 1 / CASE 2
  prompt += `Right side visual:\n`;
  prompt += `There are two possible cases, depending on whether a client reference image is attached.\n\n`;

  prompt += `CASE 1 – A client reference image IS attached:\n`;
  prompt += `- On the right side of the square canvas, create a large circular (or rounded circular segment) image area.\n`;
  prompt += `- Place the attached client image inside this circle.\n`;
  prompt += `- You MAY CROP or ZOOM the client image so it fits well inside the circle without distortion. Showing only the most relevant central part is fine.\n`;
  prompt += `- Do NOT stretch the image or expand the canvas beyond 1080x1080.\n`;
  prompt += `- Position the circle so that it overlaps slightly with the central white space and the curved colored corner shapes, but keep everything inside the square boundaries.\n\n`;

  prompt += `CASE 2 – NO client image is attached:\n`;
  prompt += `- Do NOT generate any people or object photo.\n`;
  prompt += `- Instead, use a large circular graphic shape or subtle gradient on the right to balance the left text, but keep the flyer fully typographic.\n`;
  prompt += `- Alternatively, for a fully text-centered version, you may reduce the prominence of the right side shapes and center the main offer and CTA more on the canvas.\n\n`;

  // CTA button + contact info
  prompt += `Below the main text block on the left, add a prominent call-to-action button:\n`;
  prompt += `- The button should be a rounded pill with a gold or primary-color fill and a subtle inner shadow.\n`;
  prompt += `- Use bold uppercase text with strong contrast (white text on dark button or dark text on light button).\n`;
  if (hasPhone && hasWebsite) {
    prompt += `- Button text can be "CONTACT US!" or another phrase inviting action.\n`;
  } else if (hasPhone) {
    prompt += `- If only the phone is available, use text like "CALL NOW".\n`;
  } else if (hasWebsite) {
    prompt += `- If only the website is available, use text like "VISIT WEBSITE" or "SHOP NOW".\n`;
  }

  if (hasPhone || hasWebsite) {
    prompt += `Under the button, place the contact information:\n`;
    if (hasPhone) {
      prompt += `- Show the phone number ${PHONE} with a small phone icon.\n`;
    }
    if (hasWebsite) {
      prompt += `- Show the website ${WEBSITE} with a small globe or link icon.\n`;
    }
    prompt += `Arrange this info in one or two lines, left-aligned or centered under the button, with enough spacing so it feels clear and uncluttered.\n\n`;
  } else {
    prompt += `If no contact info is provided, leave some white space under the button to keep the design balanced.\n\n`;
  }

  // Style & colors
  prompt += `Style & colors:\n\n`;
  prompt += `Default color palette if none is provided:\n`;
  prompt += `- Primary: warm golden yellow for the main offer text and CTA button.\n`;
  prompt += `- Secondary: rich orange or amber for the corner shapes and background accents.\n`;
  prompt += `- Background: clean white with subtle soft gray curves or waves to add depth.\n`;
  prompt += `- Body text: dark charcoal gray or black for maximum readability.\n\n`;
  prompt += `If custom primary and/or secondary colors are provided, apply them to the large headline, CTA button, and curved corner shapes while keeping contrast strong against the white background. Never show the hex codes as text.\n\n`;
  prompt += `Overall look: bold, high-impact, and energetic, emphasizing the promotional discount or special offer. Use clean sans-serif typography throughout, with very strong weight for the main offer and medium weight for supporting text.\n`;

  return prompt;
}

/**
 * FLYER 5: Neighborhood / Real Estate Style - House icon + circular photo
 */
export function buildFlyer5Prompt({
  INDUSTRY,
  TITLE,
  DESCRIPTION,
  DETAILS,
  PHONE,
  WEBSITE,
  PRIMARY_COLOR,
  SECONDARY_COLOR
}) {
  const hasDetails = DETAILS && DETAILS.trim().length > 0;
  const hasPhone = PHONE && PHONE.trim().length > 0;
  const hasWebsite = WEBSITE && WEBSITE.trim().length > 0;
  const hasPrimary = PRIMARY_COLOR && PRIMARY_COLOR.trim().length > 0;
  const hasSecondary = SECONDARY_COLOR && SECONDARY_COLOR.trim().length > 0;

  let prompt = '';

  prompt += `Create a friendly, modern 1080x1080 social media flyer for a ${INDUSTRY} industry, with a layout that combines a house/neighborhood theme, a circular image area, and clear contact info.\n\n`;
  prompt += `Use the following dynamic content (do NOT show label words like "Title", "Description", or "Details" in the design, only use their content):\n\n`;
  prompt += `Title (main headline): ${TITLE}\n`;
  prompt += `Description (supporting paragraph): ${DESCRIPTION}\n`;

  if (hasDetails) {
    prompt += `Details (optional extra block): ${DETAILS}.\n`;
  }

  if (hasPrimary || hasSecondary) {
    prompt += `Colors: use the custom colors provided — primary ${
      PRIMARY_COLOR || 'primary color'
    }, secondary ${
      SECONDARY_COLOR || 'secondary color'
    }. Apply them to the house icon shape, circular frame, and CTA, but NEVER print hex codes as text.\n`;
  } else {
    prompt += `Colors: if no custom colors are provided, use a warm primary color for the house elements and a cool secondary accent, as described below.\n`;
  }

  if (hasPhone) {
    prompt += `Phone (optional): ${PHONE}\n`;
  }
  if (hasWebsite) {
    prompt += `Website (optional): ${WEBSITE}\n`;
  }

  // Layout & composition
  prompt += `\nLayout and composition:\n\n`;
  prompt += `Canvas: 1080x1080. Use a clean, light background.\n\n`;

  prompt += `Top area:\n`;
  prompt += `- Place a simple house-shaped or neighborhood-themed graphic toward the top, aligned to one side (for example, left), using the primary color.\n`;
  prompt += `- Beside or slightly below it, place the main headline using the TITLE content in bold, high-contrast sans-serif text.\n`;
  prompt += `- This top section should clearly communicate the main offer or message.\n\n`;

  prompt += `Below the headline, add the DESCRIPTION as a paragraph or 2–3 short lines in dark gray text.\n`;
  prompt += `If DESCRIPTION is long, reduce the font size slightly and keep the line length comfortable; if short, use a larger size and more white space.\n\n`;

  if (hasDetails) {
    prompt += `Place the DETAILS block below the description as additional text.\n`;
    prompt += `You may lightly separate it from the description with spacing or a thin rule, but do NOT add a literal "Details" heading.\n`;
    prompt += `Preserve line breaks from the DETAILS content to keep structured info clear.\n\n`;
  }

  // Image usage: circular photo vs text-only
  prompt += `Circular visual area:\n`;
  prompt += `There are two possible cases depending on whether a client reference image is attached.\n\n`;

  prompt += `CASE 1 – A client reference image IS attached:\n`;
  prompt += `- Create a large circle on one side of the flyer (for example, right or lower-right).\n`;
  prompt += `- Place the attached client image inside this circle. You may crop or zoom slightly so it fits well, but do NOT replace it with another image.\n`;
  prompt += `- Add a thin circular border in the secondary color and possibly a small overlapping badge or icon related to the ${INDUSTRY}.\n\n`;

  prompt += `CASE 2 – NO client image is attached:\n`;
  prompt += `- Do NOT generate any photographic image.\n`;
  prompt += `- Instead, use the circular shape as a decorative graphic: a solid or gradient circle in a soft secondary tone, optionally with a simple line icon inside.\n`;
  prompt += `- You may also choose to remove the circle and center all text on the canvas for a purely typographic layout.\n\n`;

  // Contact and CTA
  if (hasPhone || hasWebsite) {
    prompt += `Contact & CTA:\n`;
    prompt += `- Near the bottom of the flyer, add a clear call-to-action button using the primary color.\n`;
    prompt += `- The button text should invite action, such as "CONTACT US", "SCHEDULE A VISIT", or something that fits the TITLE.\n`;
    prompt += `- Use bold uppercase white sans-serif text for the CTA.\n`;

    prompt += `Below or next to the button, show the contact information:\n`;
    if (hasPhone) {
      prompt += `  • Phone: display ${PHONE} with a phone icon.\n`;
    }
    if (hasWebsite) {
      prompt += `  • Website: display ${WEBSITE} with a small globe or link icon.\n`;
    }
    prompt += `Keep this info easy to read and not cramped.\n\n`;
  } else {
    prompt += `If no contact info is provided, center the bottom area with a simple tagline or additional line from the DESCRIPTION or DETAILS content.\n\n`;
  }

  // Style & colors
  prompt += `Style & colors:\n`;
  prompt += `- Default primary: a warm, inviting color (e.g., orange or terracotta) for house/neighborhood elements and CTA.\n`;
  prompt += `- Default secondary: a cooler accent (e.g., teal or soft blue) for outlines and badges.\n`;
  prompt += `- Background: light neutral, with optional soft geometric shapes.\n`;
  prompt += `- Text: dark charcoal gray or black.\n`;
  prompt += `If custom colors are provided, apply them consistently to the house icon, circular frame, CTA button, and small accents, without printing hex codes.\n`;

  prompt += `Overall look: friendly, trustworthy, and community-oriented. Use rounded corners, soft shadows, and clean sans-serif fonts to keep the design approachable.\n`;

  return prompt;
}

/**
 * Get the appropriate prompt builder for a flyer type
 */
export function getFlyerPromptBuilder(flyerType) {
  const builders = {
    type1: buildFlyer1Prompt,
    type2: buildFlyer2Prompt,
    type3: buildFlyer3Prompt,
    type4: buildFlyer4Prompt,
    type5: buildFlyer5Prompt
  };

  return builders[flyerType] || buildFlyer1Prompt;
}
